import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate, useNavigate } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { Toaster } from "sonner";
import api from "@/lib/api";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { saveProfile } from "@/lib/profiles";
import Layout from "@/components/Layout";
import AuthPage from "@/pages/AuthPage";
import Onboarding from "@/pages/Onboarding";
import Home from "@/pages/Home";
import Receipts from "@/pages/Receipts";
import ReceiptDetail from "@/pages/ReceiptDetail";
import Items from "@/pages/Items";
import ItemDetail from "@/pages/ItemDetail";
import Analytics from "@/pages/Analytics";
import Insights from "@/pages/Insights";
import Warranties from "@/pages/Warranties";
import AskAI from "@/pages/AskAI";
import Settings from "@/pages/Settings";
import Rewards from "@/pages/Rewards";
import PriceCompare from "@/pages/PriceCompare";
import Subscriptions from "@/pages/Subscriptions";
import Reports from "@/pages/Reports";
import Trips from "@/pages/Trips";
import Contact from "@/pages/Contact";
import Admin from "@/pages/Admin";
import Landing from "@/pages/Landing";
import Pricing from "@/pages/Pricing";
import PublicHPI from "@/pages/PublicHPI";
import Terms from "@/pages/Terms";
import Refunds from "@/pages/Refunds";
import InstallBanner from "@/components/InstallBanner";
import UpdateToast from "@/components/UpdateToast";
import { CircleNotch } from "@phosphor-icons/react";
import { storeRefFromUrl, claimStoredReferral } from "@/lib/referral";

function Splash() {
  // Mirrors the native PWA launch screen (branded icon on #F6F7FB) so the
  // OS splash and this React splash read as a single, seamless screen.
  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-riq-bg" data-testid="splash-screen">
      <div className="flex flex-col items-center gap-6">
        <img
          src="/icons/icon-512.png"
          alt="Sliply"
          className="w-28 h-28 rounded-[26px] drop-shadow-[0_12px_34px_rgba(110,63,252,0.28)]"
          style={{ animation: "splashPulse 1.8s ease-in-out infinite" }}
        />
        <div className="text-xs font-semibold tracking-[0.28em] uppercase text-riq-primary/70">sliply.co.za</div>
        <CircleNotch size={20} className="animate-spin text-riq-primary/50" weight="bold" />
      </div>
      <style>{`
        @keyframes splashPulse { 0%,100% { transform: scale(1); opacity: 1 } 50% { transform: scale(1.05); opacity: .9 } }
      `}</style>
    </div>
  );
}

function AuthCallback() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const processed = useRef(false);
  useEffect(() => {
    if (processed.current) return;
    processed.current = true;
    const hash = window.location.hash;
    const sid = new URLSearchParams(hash.replace("#", "")).get("session_id");
    (async () => {
      try {
        const { data } = await api.post("/auth/google/session", {}, { headers: { "X-Session-ID": sid } });
        if (data.session_token) {
          localStorage.setItem("riq_token", data.session_token);
          saveProfile(data.user, data.session_token);
        }
        setUser(data.user);
        await claimStoredReferral(api);
        window.history.replaceState(null, "", window.location.pathname);
        navigate(data.user.onboarded ? "/" : "/onboarding", { replace: true });
      } catch {
        window.history.replaceState(null, "", window.location.pathname);
        navigate("/login", { replace: true });
      }
    })();
  }, [navigate, setUser]);
  return <Splash />;
}

function Protected({ children, adminOnly }) {
  const { user } = useAuth();
  if (user === null) return <Splash />;
  if (!user) return <Navigate to="/login" replace />;
  if (!user.onboarded) return <Navigate to="/onboarding" replace />;
  if (adminOnly && user.role !== "admin") return <Navigate to="/" replace />;
  return children;
}

function RootRoute({ children }) {
  // "/" shows the marketing landing to logged-out visitors, dashboard to members.
  const { user } = useAuth();
  if (user === null) return <Splash />;
  if (!user) return <Landing />;
  if (!user.onboarded) return <Navigate to="/onboarding" replace />;
  return children;
}

function PublicOnly({ children }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const adding = new URLSearchParams(location.search).get("add") === "1";
  useEffect(() => {
    if (user && !adding) navigate(user.onboarded ? "/" : "/onboarding", { replace: true });
  }, [user, navigate, adding]);
  return children;
}

function AppRoutes() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/login" element={<PublicOnly><AuthPage mode="login" /></PublicOnly>} />
      <Route path="/register" element={<PublicOnly><AuthPage mode="register" /></PublicOnly>} />
      <Route path="/forgot" element={<PublicOnly><AuthPage mode="forgot" /></PublicOnly>} />
      <Route path="/reset" element={<AuthPage mode="reset" />} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/hpi" element={<PublicHPI />} />
      <Route path="/hpi/:month" element={<PublicHPI />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/refunds" element={<Refunds />} />
      <Route path="/onboarding" element={<OnboardingGate />} />
      <Route element={<RootRoute><Layout /></RootRoute>}>
        <Route path="/" element={<Home />} />
      </Route>
      <Route element={<Protected><Layout /></Protected>}>
        <Route path="/receipts" element={<Receipts />} />
        <Route path="/receipts/:id" element={<ReceiptDetail />} />
        <Route path="/items" element={<Items />} />
        <Route path="/items/:name" element={<ItemDetail />} />
        <Route path="/analytics" element={<Analytics />} />
        <Route path="/insights" element={<Insights />} />
        <Route path="/warranties" element={<Warranties />} />
        <Route path="/ask" element={<AskAI />} />
        <Route path="/rewards" element={<Rewards />} />
        <Route path="/prices" element={<PriceCompare />} />
        <Route path="/subscriptions" element={<Subscriptions />} />
        <Route path="/reports" element={<Reports />} />
        <Route path="/trips" element={<Trips />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/settings" element={<Settings />} />
        <Route path="/admin" element={<Protected adminOnly><Admin /></Protected>} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function OnboardingGate() {
  const { user } = useAuth();
  if (user === null) return <Splash />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.onboarded) return <Navigate to="/" replace />;
  return <Onboarding />;
}

export default function App() {
  useEffect(() => { storeRefFromUrl(); }, []);
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
          <InstallBanner />
          <UpdateToast />
          <Toaster position="top-center" richColors />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}
